import { fromJS } from 'immutable';
import {
    convertNumberToE164,
    ActiveCallDirections,
} from 'flow/utils/call-utils';

export const getActiveCallByCallId = (activeCalls, callId) => {
    return (
        activeCalls &&
        activeCalls.find((call) => {
            return call.has('callId') && call.get('callId') === callId;
        })
    );
};

export const activeCallHasStateDownAndCurrentSessionHasDirectionOutgoing = (
    activeCall,
    currentSession,
) => {
    return !!(
        activeCall &&
        activeCall.get('state') === 'down' &&
        currentSession &&
        currentSession.direction === 'outgoing'
    );
};

export const updateActiveCallWithoutDirection = (
    activeCall,
    currentSession,
    countryCode,
) => {
    if (!activeCall) {
        throw Error('Invalid active call');
    }

    if (!currentSession) {
        throw Error('Invalid current session');
    }

    // Numbers from SIP are locally formatted if they're in the
    // same country as this logged in user's number.
    // If the number is from another country, it's ITU-prefixed
    // ('00'), and have a country calling code.
    // TODO: #error-handling Validate currentSession. What to do if it is not valid?
    //
    const e164Source = convertNumberToE164(
        currentSession.remote_identity.uri.user,
        countryCode,
    );
    const e164Destination = convertNumberToE164(
        currentSession.local_identity.uri.user,
        countryCode,
    );

    return activeCall
        .set('state', 'ring')
        .set('direction', ActiveCallDirections.OUTGOING.value)
        .set('destination', e164Destination)
        .set('source', e164Source);
};

// Top starts out by setting a new call in state = down. Since we know better that we have an outgoing call from looking at our session,
// we manually set the state to ring instead. This is to avoid flickering between ring -> down -> ring on call initialization
export const setStateRingOnActiveCall = (
    activeCall,
    currentSession,
    countryCode,
) => {
    if (!activeCall) {
        return undefined;
    }

    if (
        activeCallHasStateDownAndCurrentSessionHasDirectionOutgoing(
            activeCall,
            currentSession,
        )
    ) {
        if (activeCall.has('direction')) {
            return activeCall.set('state', 'ring');
        } else {
            return updateActiveCallWithoutDirection(
                activeCall,
                currentSession,
                countryCode,
            );
        }
    }

    return activeCall;
};

export const createOutgoingActiveCall = (currentSession) => {
    if (!currentSession) {
        throw Error('Invalid current session');
    }

    const activeCall = {
        direction: ActiveCallDirections.OUTGOING.value,
        destination: currentSession.remote_identity.uri.user, // TODO: #typefix Can we ensure that this value does not throw an TypeError WEB-14418
        source: currentSession.local_identity.uri.user, // TODO: #typefix Can we ensure that this value does not throw an TypeError WEB-14418
        state: 'ring',
        key: 'activecall-999999',
    };

    return fromJS(activeCall);
};

export const getFirstActiveCallWithDirection = (activeCalls) => {
    const callsWithDirection = activeCalls?.filter((call) =>
        call.has('direction'),
    );
    const callToReturn =
        callsWithDirection && !callsWithDirection.isEmpty()
            ? callsWithDirection.first()
            : undefined;

    return callToReturn;
};

export const prioritizedActiveCall = (
    activeCalls,
    currentCallId,
    currentSession,
    countryCode,
) => {
    if ((!activeCalls || activeCalls.isEmpty()) && !currentSession) {
        return null;
    }

    // If we have an active call matching currentCallId we should set state to ring and return that call
    const prioritizedActiveCall = getActiveCallByCallId(
        activeCalls,
        currentCallId,
    );
    if (prioritizedActiveCall) {
        return setStateRingOnActiveCall(
            prioritizedActiveCall,
            currentSession,
            countryCode,
        );
    }

    // If we didn't find a prioritized active call in activeCalls we should create one with info from
    // the currentSession if the direction in the currentSession is 'outgoing'
    if (currentSession && currentSession.direction === 'outgoing') {
        return createOutgoingActiveCall(currentSession);
    }

    // If there is no currentSession, and !activeCalls, then return null
    if (!activeCalls) {
        return null;
    }

    // If we have active calls with direction => return the first active call with direction in the list
    // If we don't have calls with direction => return the first call in the active calls list
    const callWithDirection = getFirstActiveCallWithDirection(activeCalls);
    return callWithDirection || (activeCalls && activeCalls.first());
};
